import { useParams } from 'react-router-dom';
import { PATHS } from 'src/actuators/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { memo, useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { initLocationState } from 'src/actuators/locationStateContext.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { isRecommendedAccessory } from 'src/utils/isRecommendedAccessory.ts';
import { calculateTotals } from 'src/actuators/checkout/CalculateTotals.ts';
import { addToCart } from 'src/utils/addToCart.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { Device } from 'src/common/Device/Device';
import { SubTotal } from 'src/common/SubTotal/SubTotal';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Total } from 'src/common/Total/Total';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { Counter } from 'src/common/Counter/Counter';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { Price } from 'src/common/Price/Price';
import { SelectedProducts } from 'src/modules/common/components/SelectedProducts/SelectedProducts';
import {
    ActuatorAccessoriesPageData,
} from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { prepareActuatorAttributes } from 'src/actuators/prepareActuatorAttributes.ts';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { Filter } from 'src/utils/composeFilter.ts';
import { showFailSafeAttr } from 'src/actuators/showFailSafeAttr.ts';
import { NavigateFunction } from 'react-router/dist/lib/hooks';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification';
import { checkoutViewOnly } from 'src/utils/isCheckoutEnabled';
import { useLocationState } from 'src/actuators/user-input/useLocationState.ts';
import { ActuatorExportExcel } from 'src/actuators/checkout/ActuatorExportExcel.tsx';

export const ActuatorsCheckoutPage = memo(({ data, navigate, searchParams, filter } : {
    navigate: NavigateFunction;
    data: ActuatorAccessoriesPageData;
    searchParams: URLSearchParams;
    filter: Filter;
}) => {
    const {
        actuatorLabel,
        page,
    } = useParams();

    if (!actuatorLabel) {
        throw new Error('No Actuator selected');
    }

    const [locationState, setLocationState] = useLocationState();
    const { userSettings, currency } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);

    const setActuatorQty = useCallback((qty: number) => {
        setLocationState({ ...locationState, qty });
    }, [locationState, setLocationState]);

    const setAccessoryQty = useCallback((name: string, qty: number) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const deleteAccessory = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.filter((item) => item.label !== label),
        });
    }, [locationState, setLocationState]);

    const checkoutHandler = useCallback(() => {
        if (!data) {
            return;
        }
        const {
            qty,
        } = locationState;
        addToCart([
            {
                label: actuatorLabel,
                qty,
            },
            ...locationState.selectedAccessories,
        ]);
        setLocationState({
            ...initLocationState,
            sent: true,
        });
    }, [setLocationState, locationState, actuatorLabel, data]);

    const selectMoreProductsHandler = useCallback(() => {
        navigate({ pathname: PATHS.Home });
    }, [navigate]);

    const { actuator } = data.accessories;

    const {
        accessories,
        accessoriesSubtotal,
        currentCurrency,
        subtotal,
        total,
        pricePerUnit,
    } = useMemo(() => calculateTotals(
        data.accessories,
        userSettings,
        locationState,
        currency,
    ), [currency, data.accessories, locationState, userSettings]);

    const isCheckoutViewOnly = checkoutViewOnly(userSettings.shoppingCenter);

    const navigation = (
        <NavigationContainer className="vss-checkout-page__navigation-container" isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: accessories.length === 0 ? [
                        PATHS.Actuators,
                        page,
                    ].join('/') : [
                        PATHS.Accessories,
                        encodeURIComponent(actuatorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <ActuatorExportExcel
                userSettings={userSettings}
                accessories={accessories}
                actuator={actuator}
                locationState={locationState}
                showFailSafe={showFailSafeAttr(filter)}
            />

            <Button
                onClick={checkoutHandler}
                disabled={locationState.sent || isCheckoutViewOnly}
                size="lg"
                icon={(
                    <div className="vss-checkout-page__navigation-icon">
                        <ArrowRightIcon/>
                    </div>
                )}
            >
                <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
            </Button>
        </NavigationContainer>
    );

    if (locationState.sent) {
        return (
            <MainLayout>
                <MainLayoutHeader titleId={MESSAGES.selectedProductsTitle} navigation={null} breadcrumbs={null}/>

                <SelectedProducts onButtonClick={selectMoreProductsHandler}/>
            </MainLayout>
        );
    }

    return (
        <div className="vss-actuators">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.checkoutPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={null}
                />

                {isCheckoutViewOnly && (
                    <InlineNotification
                        type="warning"
                        message={<I18n messageId={MESSAGES.pleaseContact}/>}
                    />
                )}

                <div className="vss-actuators-checkout-page">
                    <h2 className="vss-actuators-checkout-page__title">
                        <I18n messageId={MESSAGES.checkoutActuatorTitle}/>
                    </h2>

                    <Device
                        defaultImage={<ActuatorIcon/>}
                        product={actuator}
                        attributes={prepareActuatorAttributes(actuator, showFailSafeAttr(filter))}
                        price={isCheckoutViewOnly ? '\u2014' : (
                            <Price
                                currency={currentCurrency}
                                price={pricePerUnit}
                                locale={userSettings.currentLocale}
                            />
                        )}
                        actions={(
                            <Counter
                                value={locationState.qty}
                                onChange={setActuatorQty}
                            />
                        )}
                    />

                    <SubTotal
                        isMobile={isMobile}
                        price={isCheckoutViewOnly ? null : subtotal}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {accessories.length > 0 && (
                        <>
                            <h2 className="vss-actuators-checkout-page__title">
                                <I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>
                            </h2>

                            {accessories
                                .map(({ item }) => {
                                    const stateRow = locationState
                                        .selectedAccessories.find(({ label }) => label === item.Label);
                                    const qty = stateRow?.qty ?? 1;
                                    const price = getPriceValue(
                                        item,
                                        userSettings.shoppingCenter as keyof typeof item,
                                        currency,
                                    );
                                    return (
                                        <Accessory
                                            chip={isRecommendedAccessory(item.zOption)
                                                ? <I18n messageId={MESSAGES.recommended}/> : null}
                                            accessory={item}
                                            key={`${item.Label}-${item.ID}`}
                                            price={isCheckoutViewOnly ? '\u2014' : (
                                                <Price
                                                    price={price ? price.value : 0}
                                                    currency={currentCurrency}
                                                    locale={userSettings.currentLocale}
                                                />
                                            )}
                                        >
                                            <div className="vss-checkout-page__action-wrapper">
                                                <div className="vss-checkout-page__price-wrapper">
                                                    <Button
                                                        className="vss-checkout-page__button"
                                                        kind="ghost"
                                                        iconOnly={true}
                                                        onClick={() => deleteAccessory(item.Label)}
                                                    >
                                                        <div className="vss-checkout-page__icon-wrapper">
                                                            <DeleteIcon/>
                                                        </div>
                                                    </Button>
                                                </div>

                                                <div className="vss-checkout-page__counter">
                                                    <Counter
                                                        value={qty}
                                                        onChange={(value) => setAccessoryQty(item.Label, value)}
                                                    />
                                                </div>
                                            </div>
                                        </Accessory>
                                    );
                                })}
                            <SubTotal
                                isMobile={isMobile}
                                price={isCheckoutViewOnly ? null : accessoriesSubtotal}
                                locale={userSettings.currentLocale}
                                currency={currentCurrency}
                            />
                        </>
                    )}

                    <Total
                        price={isCheckoutViewOnly ? null : total}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />
                </div>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
});
